import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'
import { useEffect } from 'react'
import { router } from '@/router'
import { useAuthStore } from '@/stores/auth'
import { consumeAuthHandoff } from '@/lib/authHandoff'
import './index.css'

/** Org panel iframe (?embed=1) or framed load — keep boot lean. */
function isEmbedBoot(): boolean {
  try {
    const q = new URLSearchParams(window.location.search)
    if (q.get('embed') === '1') return true
    return window.self !== window.top
  } catch {
    return false
  }
}

const embedBoot = isEmbedBoot()

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // Session replay is heavy; skip inside Filament embeds.
    ...(embedBoot ? [] : [Sentry.replayIntegration()]),
  ],
  tracesSampleRate: embedBoot ? 0.05 : 0.2,
  replaysSessionSampleRate: embedBoot ? 0 : 0.05,
  replaysOnErrorSampleRate: embedBoot ? 0 : 1.0,
})

async function bootstrap() {
  // Filament embeds: start loading org tool chunks in parallel with auth so the
  // lazy route does not wait after login (main embed lag source).
  const orgToolPreload = embedBoot
    ? Promise.all([
        import('@/views/OrgSOSView'),
        import('@/views/OrgLocationView'),
        import('@/views/OrgCameraView'),
      ]).catch(() => undefined)
    : Promise.resolve(undefined)

  // If opened from org-panel with #handoff=, sign into Firebase first so
  // staff are not prompted to log in again (seamless deep links).
  await consumeAuthHandoff()

  // Auth must resolve before the router mounts.
  // Router guards fire on first navigation — if auth is unresolved,
  // every refresh redirects to landing even for logged-in users.
  await useAuthStore.getState().init()

  // Prefer having chunks ready before first paint of the org tool.
  if (embedBoot) {
    await orgToolPreload
  }

  const rootEl = document.getElementById('root')
  if (!rootEl) throw new Error('Root element not found')

  createRoot(rootEl).render(
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 gap-3 px-6 text-center">
          <p className="text-slate-700 font-semibold">Something went wrong.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-beacon-600 text-white text-sm font-semibold"
          >
            Reload David's Beacon
          </button>
        </div>
      }
    >
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>,
  )
}

bootstrap()
  .then(() => {
    // Successful boot — allow future stale-chunk auto-reloads
    setTimeout(() => sessionStorage.removeItem('chunk_error_reloaded'), 10000)
    if (!embedBoot) {
      // Prefetch Leaflet views after interactive (family app only)
      setTimeout(() => {
        void import('@/views/LocationView')
        void import('@/views/HazardsView')
        void import('@/views/OrgLocationView')
      }, 3000)
    }
  })
  .catch((err: unknown) => {
    Sentry.captureException(err)
    console.error('[Beacon] Bootstrap failed:', err)
  })
